import Button from '@hilbi/web-common/components/e-visits/button';
import { TagSelect } from '@hilbi/web-common/components/e-visits/select/select';
import TextArea from '@hilbi/web-common/components/e-visits/text-area';
import { HilbiForm } from '@hilbi/web-common/components/hilbi-form';
import FormField from '@hilbi/web-common/components/hilbi-form/form-field/form-field';
import { endOfDay, getDay, isAfter, isBefore, startOfDay } from 'date-fns';
import { useFormik } from 'formik';
import moment from 'moment';
import React, { useEffect, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { ReactComponent as Delete } from '../../../../../../assets/svg/calender_menu_delete.svg';
import { ReactComponent as Close } from '../../../../../../assets/svg/calender_menu_x.svg';
import { ReactComponent as EditIcon } from '../../../../../../assets/svg/edit_e-visit.svg';
import { ReactComponent as RiskIcon } from '../../../../../../assets/svg/icon-risk-orange.svg';
import Checkbox from '../../../../../../components/checkbox';
import { DateTimePicker, RangePickerDeprecated } from '../../../../../../components/ui';
import { IAddConfig, ICalendarEvent } from '../../../../../../components/ui/calendar/calendars';
import { getShortDateFormat } from '../../../../../../helpers/utils-helpers';
import { actions as eVisitActions } from '../../../../../../store/ducks/e-visits';
import { TExistingEVisitRule } from '../../../../../../types/e-visits';
import TStore from '../../../../../../types/store';
import { fitAllBlocksInInterval } from '../../../../utils';
import { MenuHeaderSC } from '../../../menu-components/menu-header/menu-header';
import BlockSizeSelector from '../block-size-selector';
import PreviewForm from '../preview-form';
import { IFormValues, validationSchema } from './add-form.schema';
import { AddFormSC } from './add-form.styled';
import { generateDisabledTimes, useInitialValues } from './add-form.util';

const CalendarIcon = (
  <div
    style={{
      width: '100%',
      height: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}
  >
    <img
      src={require('../../../../../../assets/svg/icon-datepicker.svg').default}
      alt=""
      style={{ width: 16, height: 16 }}
    />
  </div>
);

interface AddFormProps {
  event: ICalendarEvent<TExistingEVisitRule>;
  onClose: (force?: boolean) => void;
  horizontalPosition?: 'left' | 'right';
  onAdd?: (config: IAddConfig<TExistingEVisitRule>) => void;
  onSubmit: (values: IFormValues, originalEvent: ICalendarEvent<TExistingEVisitRule>) => void;
  onDelete: (event: ICalendarEvent<TExistingEVisitRule>) => void;
}

const AddForm: React.FunctionComponent<AddFormProps> = ({
  event,
  onClose,
  horizontalPosition,
  onAdd,
  onSubmit,
  onDelete,
}) => {
  const [editMode, setEditMode] = React.useState(!event.eventData?.id);
  const [originalEvent] = React.useState(event);

  const dispatch = useDispatch();
  const tagOptions = useSelector((state: TStore) => state.eVisits.tagOptions);

  useEffect(() => {
    dispatch(eVisitActions.getTagsRequest());
    return () => {
      dispatch(eVisitActions.clearTag());
    };
  }, [dispatch]);

  const initialValues = useInitialValues(event);

  const { values, setValues, setFieldValue, handleChange, handleSubmit, isValid, errors } =
    useFormik({
      initialValues,
      validationSchema,
      onSubmit: (values) => onSubmit(values, originalEvent),
      enableReinitialize: true,
    });

  React.useEffect(() => {
    setValues({
      ...values,
      fromTime: event.start,
      toTime: event.end,
    });
  }, [event.start, event.end]);

  const disabledTimes = React.useMemo(() => {
    return generateDisabledTimes({
      start: new Date(event.start),
      minTime: event.minTime ? new Date(event.minTime) : undefined,
      maxTime: event.maxTime ? new Date(event.maxTime) : undefined,
    });
  }, [event.start, event.maxTime, event.minTime]);

  const dayNumber = getDay(new Date(values.fromTime));
  const { fullBlocks, emptySpace } = fitAllBlocksInInterval(
    new Date(values.fromTime),
    new Date(values.toTime),
    values.blockSize,
  );

  const isInPast = isBefore(startOfDay(new Date(event.start)), startOfDay(Date.now()));
  const headerActions: any = useMemo(() => {
    const _actions = [
      {
        icon: <Close />,
        onClick: () => {
          onClose(true);
        },
      },
    ];

    if (!editMode && !isInPast) {
      _actions.unshift({
        icon: <EditIcon />,
        onClick: () => {
          onAdd?.({
            ...event,
            enableGestures: true,
          });
          setEditMode(true);
        },
      });
    }

    if (event.eventData?.id && !isInPast) {
      _actions.unshift({
        icon: <Delete />,
        onClick: () => {
          onDelete(originalEvent);
        },
      });
    }

    return _actions.map((action, i) => {
      return (
        <button key={i} className="action" type="button" onClick={action.onClick}>
          {React.cloneElement(action.icon)}
        </button>
      );
    });
  }, [isInPast, editMode, event]);

  const { formatMessage } = useIntl();

  return (
    <AddFormSC position={horizontalPosition || 'left'} onSubmit={handleSubmit}>
      <MenuHeaderSC horizontalPosition={horizontalPosition}>
        <div className="title-container">{moment(event.start).format('dddd')}</div>
        <div className="actions">{headerActions}</div>
      </MenuHeaderSC>
      {editMode ? (
        <HilbiForm variation={'lined'}>
          <div className="content">
            <BlockSizeSelector
              value={values.blockSize}
              onChange={(v) => {
                setFieldValue('blockSize', v);
                onAdd?.({
                  ...event,
                  // @ts-ignore
                  eventData: {
                    ...event.eventData,
                    blockSize: v,
                  },
                });
              }}
            />
            <div className="time-wrapper">
              <label>
                <FormattedMessage id="addForm_time_label" />
              </label>
              <RangePickerDeprecated
                picker="time"
                generateConfig="moment"
                showSecond={false}
                showNow={false}
                format="HH:mm"
                kind="bubble"
                disabledHours={disabledTimes?.disabledHours}
                disabledMinutes={disabledTimes?.disabledMinutes}
                value={[moment(values.fromTime), moment(values.toTime)]}
                onChange={(dates) => {
                  let fromTime = dates?.[0] ? dates?.[0]?.toISOString() : values.fromTime;
                  let toTime = dates?.[1] ? dates?.[1]?.toISOString() : values.toTime;

                  if (!fromTime || !toTime) {
                    return;
                  }

                  fromTime = event.minTime
                    ? isAfter(dates![0]!.toDate(), new Date(event.minTime))
                      ? fromTime!
                      : event.minTime
                    : fromTime!;

                  toTime = event.maxTime
                    ? isAfter(dates![1]!.toDate(), new Date(event.maxTime))
                      ? event.maxTime
                      : toTime!
                    : toTime!;

                  setValues({
                    ...values,
                    fromTime,
                    toTime,
                  });
                  onAdd?.({
                    start: fromTime,
                    end: toTime,
                    eventData: event.eventData,
                    minTime: event.minTime,
                    maxTime: event.maxTime,
                  });
                }}
                minuteStep={15}
                hideDisabledOptions
              />
              <div className="time-pill">
                <FormattedMessage id="addForm_count_info" values={{ count: fullBlocks }} />
              </div>
            </div>
            <FormField
              label={formatMessage({
                id: 'addForm_description',
              })}
              error={errors['description']}
            >
              <TextArea
                name={'description'}
                onChange={(v) => {
                  setValues({ ...values, description: v.currentTarget.value });
                }}
                value={values['description'] || ''}
                placeholder={formatMessage({
                  id: 'addForm_description_placeholder',
                })}
              />
            </FormField>
            <TagSelect
              tagOptions={tagOptions}
              name={'selectedTag'}
              disabled={false}
              value={values['selectedTag']}
              setValue={(v) => {
                setValues({ ...values, selectedTag: v });
                dispatch(eVisitActions.setSelectedTag(v));
              }}
              error={errors['selectedTag']}
            />
            <Checkbox
              name="repeat"
              value={values.repeat as any}
              onChange={(e) => {
                setValues({
                  ...values,
                  repeat: e.target.checked,
                  repeatTo: false,
                  toDate: undefined,
                });
              }}
              className="small"
            >
              <FormattedMessage
                id="addForm_repeat"
                values={{
                  day: moment(event.start).format('dddd'),
                  dayNumber,
                }}
              />
            </Checkbox>
            {values.repeat && (
              <div className="repeat-form">
                <div className="item">
                  <Checkbox
                    name="repeatTo"
                    value={values.repeatTo as any}
                    onChange={(e) => {
                      if (!e.target.checked) {
                        setValues({
                          ...values,
                          repeatTo: false,
                          toDate: undefined,
                        });
                      } else {
                        setValues({
                          ...values,
                          repeatTo: true,
                          toDate: endOfDay(new Date(event.start)).toISOString(),
                        });
                      }
                    }}
                    className="small"
                  >
                    <FormattedMessage id="addForm_repeatTo" />
                  </Checkbox>
                  <div style={{ opacity: values.repeatTo ? 1 : 0.6 }}>
                    <DateTimePicker
                      generateConfig="moment"
                      kind="inline"
                      format={getShortDateFormat()}
                      picker="date"
                      value={values.toDate ? moment(values.toDate) : null}
                      onChange={(date) => setFieldValue('toDate', date?.toISOString())}
                      suffixIcon={CalendarIcon}
                      disabled={!values.repeatTo}
                      disabledDate={(d) =>
                        d.isBefore(Date.now(), 'days') ||
                        d.day() !== dayNumber ||
                        (!!values.fromDate && d.isBefore(values.fromDate, 'days'))
                      }
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
          <div className="footer">
            {(emptySpace || fullBlocks === 0) && (
              <div className="count-error">
                <RiskIcon />
                <FormattedMessage id="addForm_count_warn" />
              </div>
            )}
            <div className="form-actions">
              <Button
                variation={'secondary'}
                isBlock
                onClick={() => {
                  onClose(true);
                }}
              >
                <FormattedMessage id="addForm_cancel_action" />
              </Button>
              <Button type="submit" variation={'primary'} isBlock disabled={emptySpace || !isValid}>
                <FormattedMessage id="addForm_submit_action" />
              </Button>
            </div>
          </div>
        </HilbiForm>
      ) : (
        <PreviewForm event={event} onClose={() => onClose(true)} />
      )}
    </AddFormSC>
  );
};

export default React.memo(AddForm);
